/**
 * Week arithmetic for the rota — STORY-094.
 *
 * **ISO-8601 throughout, and never a raw `getDay()`.** JavaScript numbers
 * Sunday zero and the database numbers Monday one; the two agree for five days
 * a week, and Sunday is the day that catches it. The server does the same
 * conversion, in `staff.service.ts`, for the same reason.
 *
 * **All of it in UTC.** A `date` column carries no zone, so a week boundary
 * computed in the browser's local time would land on the wrong Monday for
 * anybody west of Greenwich after four in the afternoon.
 */

const DAY = 24 * 60 * 60 * 1000;

/** ISO-8601: 1 = Monday … 7 = Sunday. */
export function isoWeekdayOf(date: Date): number {
  return date.getUTCDay() === 0 ? 7 : date.getUTCDay();
}

/** The Monday of whatever week a `YYYY-MM-DD` falls in. */
export function mondayOf(iso: string): string {
  const date = new Date(`${iso}T00:00:00.000Z`);
  return isoOf(new Date(date.getTime() - (isoWeekdayOf(date) - 1) * DAY));
}

/** The Monday `weeks` away — negative goes back. */
export function shiftWeeks(monday: string, weeks: number): string {
  return isoOf(new Date(new Date(`${monday}T00:00:00.000Z`).getTime() + weeks * 7 * DAY));
}

/** This week's Monday. */
export function thisMonday(): string {
  return mondayOf(isoOf(new Date()));
}

/**
 * `2026-09-07` → `7 – 13 Sep`, or `28 Sep – 4 Oct` across a month boundary.
 *
 * The month is named once when the week does not cross one, because *7 Sep –
 * 13 Sep* reads as two dates rather than as a range.
 */
export function weekLabel(monday: string): string {
  const from = new Date(`${monday}T00:00:00.000Z`);
  const to = new Date(from.getTime() + 6 * DAY);
  const sameMonth = from.getUTCMonth() === to.getUTCMonth();

  const start = sameMonth
    ? String(from.getUTCDate())
    : `${String(from.getUTCDate())} ${MONTHS[from.getUTCMonth()] ?? ""}`;

  return `${start} – ${String(to.getUTCDate())} ${MONTHS[to.getUTCMonth()] ?? ""} ${String(
    to.getUTCFullYear(),
  )}`;
}

function isoOf(date: Date): string {
  return date.toISOString().slice(0, 10);
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * Whether a local date has already finished — Anthony's QA, item 5.
 *
 * **The line is the start of today, not this instant**, exactly as
 * `isInThePast` draws it on the server for bookings: same-day changes are
 * ordinary and yesterday is the thing worth asking about.
 *
 * Compared as `yyyy-mm-dd` strings, which sort lexicographically in the same
 * order they sort chronologically — no `Date` arithmetic, so no timezone can
 * shift the boundary by a few hours the way parsing to an instant would.
 */
export function isPast(date: string): boolean {
  const now = new Date();
  const today = [
    String(now.getFullYear()),
    String(now.getMonth() + 1).padStart(2, "0"),
    String(now.getDate()).padStart(2, "0"),
  ].join("-");

  return date < today;
}
